import { MRT_Row } from 'material-react-table'
import { RefObject } from 'react'

import { IsDisabledParams } from '@/components/FieldRenderer/types'
import { renumberOrderInPlace } from '@/utils/collectionOrder'

import { MuiRowDragHandleProps, SetGeneratedCode } from './types'

/**
 * Find and focus the next or previous focusable cell in the table (tab order: row by row).
 * Used when Tab/Shift+Tab is pressed in an expanded inline editor to close it and move focus.
 */
export const focusAdjacentTableCell = (
	cellContainerRef: RefObject<HTMLElement | null>,
	direction: 'next' | 'prev',
) => {
	const currentTd = cellContainerRef.current?.closest('td') as HTMLTableCellElement | null
	if (!currentTd) return

	const tbody = currentTd.closest('tbody')
	if (!tbody) return

	const rows = Array.from(tbody.querySelectorAll('tr[data-index]')) as HTMLTableRowElement[]
	const currentRow = currentTd.closest('tr') as HTMLTableRowElement
	const currentRowIndex = rows.indexOf(currentRow)
	if (currentRowIndex === -1) return

	const getCellsInRow = (row: HTMLTableRowElement) => {
		return Array.from(row.querySelectorAll('td')).filter((cell) => {
			const isActionColumn =
				cell.querySelector('[data-testid*="action"]') || cell.classList.contains('MuiTableCell-alignRight')
			const isDragColumn =
				cell.querySelector('.MuiTableSortLabel-root') === null && cell.textContent?.trim() === ''
			return !isActionColumn && !isDragColumn
		}) as HTMLTableCellElement[]
	}

	const cellsInCurrentRow = getCellsInRow(currentRow)
	const currentCellIndex = cellsInCurrentRow.indexOf(currentTd)
	if (currentCellIndex === -1) return

	let targetCell: HTMLTableCellElement | null = null
	if (direction === 'next') {
		if (currentCellIndex < cellsInCurrentRow.length - 1) {
			targetCell = cellsInCurrentRow[currentCellIndex + 1]
		} else if (currentRowIndex < rows.length - 1) {
			const nextRow = rows[currentRowIndex + 1]
			targetCell = getCellsInRow(nextRow)[0] ?? null
		}
	} else {
		if (currentCellIndex > 0) {
			targetCell = cellsInCurrentRow[currentCellIndex - 1]
		} else if (currentRowIndex > 0) {
			const prevRow = rows[currentRowIndex - 1]
			const prevCells = getCellsInRow(prevRow)
			targetCell = prevCells[prevCells.length - 1] ?? null
		}
	}

	if (!targetCell) return

	// Prefer real inputs so the user gets a visible text cursor; fallback to cell trigger [tabindex="0"]
	const input = targetCell.querySelector(
		'input:not([type="hidden"]), textarea, [contenteditable="true"]',
	) as HTMLElement | null
	const trigger = targetCell.querySelector('[tabindex="0"]') as HTMLElement | null
	const toFocus = input ?? trigger
	if (!toFocus) return

	toFocus.focus()
	toFocus.scrollIntoView({ block: 'nearest', behavior: 'smooth' })

	if (input) {
		if (input.tagName === 'INPUT' && (input as HTMLInputElement).type === 'text') {
			;(input as HTMLInputElement).select()
		}
	} else if (trigger) {
		// Expandable cell: trigger open so the editor gets focus and shows cursor
		requestAnimationFrame(() => {
			trigger.click()
		})
	}
}

export const muiRowDragHandleProps = <TData extends object>({
	updateTableData,
	table,
	editMode,
}: MuiRowDragHandleProps<TData>) => ({
	style: {
		display: editMode ? 'flex' : 'none',
		justifyContent: 'center',
		alignItems: 'center',
		width: '100%',
		cursor: editMode ? 'grab' : 'default',
	},
	onDragEnd: () => {
		if (!editMode) return

		const { draggingRow, hoveredRow } = table.getState()
		if (!hoveredRow || !draggingRow) return

		const fromIndex = draggingRow.index
		const toIndex = (hoveredRow as MRT_Row<TData>).index
		if (fromIndex === toIndex) return

		updateTableData((draft) => {
			const [removed] = draft.splice(fromIndex, 1)
			if (!removed) return
			// Insert at toIndex: when moving down this places the row after the hovered row (so it can become last); when moving up it places before the hovered row.
			draft.splice(toIndex, 0, removed)
			renumberOrderInPlace(draft as { order?: number }[])
		})
	},
})

export const getInitCellValue = <TData>(
	customValue: string | number | boolean | undefined,
	cellValue: TData,
) => {
	if (!customValue && !cellValue) return ''

	return customValue ?? cellValue
}

export const getMrtTableDisabled = <TData extends object, TValue = any>({
	column,
	editMode,
	row,
	isPanelOpen,
}: IsDisabledParams<TData, TValue>) => {
	const isDisabledMeta = column?.columnDef?.meta?.isDisabled

	if (isPanelOpen) return true

	if (!editMode) {
		return true
	}

	if (isDisabledMeta && typeof isDisabledMeta === 'function') {
		return isDisabledMeta(row)
	}

	return false
}

export const setGeneratedCode: SetGeneratedCode = async ({
	adjustTableNameMutation,
	fieldValue,
	isUpperCase,
	setFormValue,
}) => {
	const response = await adjustTableNameMutation({
		utilsNameRequestDto: { name: fieldValue },
	}).unwrap()
	const generatedCode = response.result
	const formattedCode = isUpperCase
		? generatedCode
		: generatedCode.toLowerCase()

	setFormValue('code', formattedCode, {
		shouldValidate: true,
	})
}
